'use client';

import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const AUTO_PLAY_MS = 5000;

/** Below Tailwind's `md` breakpoint the carousel is considered mobile. */
const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

/** Default per-slide widths: 1 card on mobile, 2 on md, 3 on lg (Destaques layout). */
const DEFAULT_ITEM_BASIS_CLASS = 'basis-full md:basis-1/2 lg:basis-1/3';

type FeaturedProductsCarouselProps = {
  children: ReactNode;
  previousLabel: string;
  nextLabel: string;
  alwaysShowButtons?: boolean;
  /** false = manual only; true = always auto-advance; 'mobile' = auto-advance only below md. */
  autoPlay?: boolean | 'mobile';
  /** Interval between auto-advances; loops back to the first slide after the last. */
  autoPlayIntervalMs?: number;
  /** Snaps advanced per auto-play tick (manual nav always moves 1). */
  autoPlayStep?: number;
  /** Touch screens have no hover — show the nav buttons without it below md. */
  showButtonsOnMobile?: boolean;
  /** Responsive `basis-*` classes controlling how many cards show per view. */
  itemBasisClassName?: string;
};

export function FeaturedProductsCarousel({
  children,
  previousLabel,
  nextLabel,
  alwaysShowButtons = false,
  autoPlay = true,
  autoPlayIntervalMs = AUTO_PLAY_MS,
  autoPlayStep = 1,
  showButtonsOnMobile = false,
  itemBasisClassName = DEFAULT_ITEM_BASIS_CLASS,
}: FeaturedProductsCarouselProps) {
  const isPausedRef = useRef(false);
  const [api, setApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const childArray = Children.toArray(children);

  useEffect(() => {
    if (autoPlay !== 'mobile') return;
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const update = () => setIsMobileViewport(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, [autoPlay]);

  const autoPlayEnabled =
    autoPlay === true || (autoPlay === 'mobile' && isMobileViewport);

  const updateScrollButtons = useCallback((carouselApi: CarouselApi | undefined) => {
    if (!carouselApi) return;
    setCanScrollPrev(carouselApi.canScrollPrev());
    setCanScrollNext(carouselApi.canScrollNext());
  }, []);

  const advanceSlide = useCallback(() => {
    if (!api) return;
    if (!api.canScrollNext()) {
      api.scrollTo(0);
      return;
    }
    const lastSnap = api.scrollSnapList().length - 1;
    api.scrollTo(Math.min(api.selectedScrollSnap() + autoPlayStep, lastSnap));
  }, [api, autoPlayStep]);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => updateScrollButtons(api);
    updateScrollButtons(api);
    api.on('select', onSelect);
    api.on('reInit', onSelect);

    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
    };
  }, [api, updateScrollButtons]);

  useEffect(() => {
    if (!api || !autoPlayEnabled) return;

    const snapCount = api.scrollSnapList().length;
    if (snapCount <= 1) return;

    let rafId = 0;
    let slideStart = performance.now();

    const tick = (now: number) => {
      if (isPausedRef.current) {
        slideStart = now;
        rafId = requestAnimationFrame(tick);
        return;
      }

      const elapsed = now - slideStart;
      if (elapsed >= autoPlayIntervalMs) {
        advanceSlide();
        return;
      }

      rafId = requestAnimationFrame(tick);
    };

    const startTick = () => {
      cancelAnimationFrame(rafId);
      slideStart = performance.now();
      rafId = requestAnimationFrame(tick);
    };

    const onSelect = () => startTick();

    api.on('select', onSelect);
    api.on('reInit', onSelect);
    startTick();

    return () => {
      cancelAnimationFrame(rafId);
      api.off('select', onSelect);
      api.off('reInit', onSelect);
    };
  }, [api, advanceSlide, autoPlayEnabled, autoPlayIntervalMs]);

  // Keep -translate-y-1/2 on :active — Button's default active:translate-y-px
  // replaces the whole translate and makes the control jump down on press.
  const navButtonClass = cn(
    'absolute top-1/2 z-10 size-7 -translate-y-1/2 active:not-aria-[haspopup]:-translate-y-1/2',
    'rounded-full border-0 shadow-none',
    'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    alwaysShowButtons
      ? 'opacity-100'
      : showButtonsOnMobile
        ? 'opacity-100 transition-opacity duration-200 md:opacity-0 md:group-hover:opacity-100'
        : 'opacity-0 transition-opacity duration-200 group-hover:opacity-100',
    'focus-visible:opacity-100'
  );

  return (
    <div
      className="group relative overflow-visible py-1 pb-2"
      onMouseEnter={() => {
        isPausedRef.current = true;
      }}
      onMouseLeave={() => {
        isPausedRef.current = false;
      }}
      onFocusCapture={() => {
        isPausedRef.current = true;
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          isPausedRef.current = false;
        }
      }}
    >
      {canScrollPrev && (
        <Button
          type="button"
          variant="secondary"
          size="icon-sm"
          className={cn(navButtonClass, '-left-5')}
          onClick={() => api?.scrollPrev()}
          aria-label={previousLabel}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      )}
      {canScrollNext && (
        <Button
          type="button"
          variant="secondary"
          size="icon-sm"
          className={cn(navButtonClass, '-right-5')}
          onClick={() => api?.scrollNext()}
          aria-label={nextLabel}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}
      <Carousel setApi={setApi} opts={{ align: 'start' }} className="w-full">
        <CarouselContent className="-ml-3">
          {childArray.map((child, index) => (
            <CarouselItem
              key={isValidElement(child) ? child.key ?? index : index}
              className={cn('pl-3', itemBasisClassName)}
            >
              <div className="px-0.5">{child}</div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
}
